import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject, takeLast, takeUntil } from 'rxjs';

import {
  FilterCategories,
  IBodyScanDoctors,
  IBodyScanKeywords,
  IDoctors,
  IKeywords,
  MajorCategories,
  MHCity,
} from '@alpha/types';
import {
  AppLogger,
  Button,
  Checkbox,
  FilterType,
  ICardGroupItem,
  ICardItem,
  IFilterItem,
  RadioButton,
  TranformService,
  Util,
} from 'app-core';
import {
  SearchService,
  CacheService,
  FilterService,
  UIUtilService,
} from 'app-service';

import { ClinicSearchFilter } from './search-clinic-filter.component';
import { ScanClinicSearchFilter } from './search-scan-clinic-filter.component';

import ButtonType = Data.Core.ButtonType;
import CardType = Data.Core.CardType;
import IconType = Data.Core.IconType;

@Component({
  selector: 'app-search-home',
  templateUrl: './search-home.component.html',
  styleUrls: ['./search-home.component.scss'],
  viewProviders: [SearchService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchHomeComponent implements OnInit, OnDestroy {
  public showFiltersButton: Button = new Button({
    label: 'Filters',
    type: ButtonType.flatButton,
    icon: IconType.filterIcon,
    action: () => this._showFilters(),
  });
  public isListEmpty: boolean = true;
  public readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  public locationItems: Array<RadioButton> = [];
  public loading: boolean = false;
  public filterItems: Array<IFilterItem> = [];
  public cards: Array<ICardGroupItem> = [];
  public radioFilter: FilterType = 'RadioButton';
  public loadMoreButton: Button = new Button({
    label: 'Load More Results',
    type: ButtonType.button,
    action: () => {
      if (!this.isListEmpty) {
        this._pageCounter = this._pageCounter + 1;
        this.loadMore = true;

        if (Number(this.currentQueryParams['c']) === MajorCategories.Doctor) {
          this._getDoctorsList(this._pageCounter, this._query);
        }

        if (Number(this.currentQueryParams['c']) === MajorCategories.BodyScan) {
          this._getBodyScanDoctorsList(this._pageCounter, this._query);
        }
      }
    },
  });
  private loadMore: boolean = false;
  private _pageCounter: number = 1;
  private _query: Partial<IKeywords> = {};
  private listObj: Array<ICardItem> = [];
  public locationSubDistrict: number = MHCity.Pune;
  private currentQueryParams: Params = {};

  public constructor(
    public readonly _cdRef: ChangeDetectorRef,
    private readonly _cacheService: CacheService,
    private readonly _filterService: FilterService,
    private readonly _route: ActivatedRoute,
    private readonly _searchService: SearchService,
    private readonly _tranformService: TranformService,
    private readonly _router: Router,
    public readonly _uiUtilService: UIUtilService,
  ) {
    this._uiUtilService.isClearFilter.pipe().subscribe((result) => {
      if (result) {
        this.ngOnInit();
      }
    });
    // Extract existing query params
    this._route.queryParams.subscribe((params) => {
      this.currentQueryParams = params;
    });
  }

  public ngOnInit(): void {
    this._route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((paramMap: Params) => {
        this._query = Util.processParameters({ params: paramMap });
        this.loadMore = false;
        this._pageCounter = 1;
        if (!!this._cacheService.getItem('l')) {
          this._query.location!.subDistrict = [
            Number(this._cacheService.getItem('l')),
          ];

          if (Number(this.currentQueryParams['c']) === MajorCategories.Doctor) {
            this._getDoctorsList(this._pageCounter, this._query);
          }

          if (
            Number(this.currentQueryParams['c']) === MajorCategories.BodyScan
          ) {
            this._getBodyScanDoctorsList(this._pageCounter, this._query);
          }
          if (
            this.locationSubDistrict !== this._query.location?.subDistrict[0]
          ) {
            this.locationSubDistrict =
              this._query.location?.subDistrict[0] || MHCity.Pune;
            if (
              Number(this.currentQueryParams['c']) === MajorCategories.Doctor
            ) {
              this.getClinicLocationFilter(this.locationSubDistrict);
            }

            if (
              Number(this.currentQueryParams['c']) === MajorCategories.BodyScan
            ) {
              this.getBodyScanLocationFilter(this.locationSubDistrict);
            }
          }
        }

        if (Number(this.currentQueryParams['c']) === MajorCategories.Doctor) {
          this._loadClinicFilter();
        }

        if (Number(this.currentQueryParams['c']) === MajorCategories.BodyScan) {
          this._loadScanClinicFilter();
        }
      });

    this._uiUtilService.isLocationChanged
      .pipe(takeLast(1), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (!!this._cacheService.getItem('l')) {
          this._query.location!.subDistrict = [
            Number(this._cacheService.getItem('l')),
          ];

          if (Number(this.currentQueryParams['c']) === MajorCategories.Doctor) {
            this._getDoctorsList(this._pageCounter, this._query);
          }

          if (
            Number(this.currentQueryParams['c']) === MajorCategories.BodyScan
          ) {
            this._getBodyScanDoctorsList(this._pageCounter, this._query);
          }

          if (
            this.locationSubDistrict !== this._query.location?.subDistrict[0]
          ) {
            this.locationSubDistrict =
              this._query.location?.subDistrict[0] || MHCity.Pune;
            if (
              Number(this.currentQueryParams['c']) === MajorCategories.Doctor
            ) {
              this.getClinicLocationFilter(this.locationSubDistrict);
            }

            if (
              Number(this.currentQueryParams['c']) === MajorCategories.BodyScan
            ) {
              this.getBodyScanLocationFilter(this.locationSubDistrict);
            }
          }
        }
      });

    this._uiUtilService.isNavbarOpened.next(false);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private _showFilters(): void {
    this._uiUtilService.isNavbarOpened.next(true);
  }

  private _loadClinicFilter(): void {
    const indexToKeep = this.filterItems.findIndex(
      (filter) => filter.title === 'Location',
    );

    const clinicFilter = this.filterItems.some(
      (filter) => filter.title === 'Specialities',
    );
    if (indexToKeep !== -1 && !clinicFilter) {
      this.getClinicFilters(indexToKeep);
    } else if (indexToKeep === -1 && !clinicFilter) {
      this.getClinicFilters(indexToKeep);
    }
  }

  private _loadScanClinicFilter(): void {
    const indexToKeep = this.filterItems.findIndex(
      (filter) => filter.title === 'Location',
    );

    const clinicFilter = this.filterItems.some(
      (filter) => filter.title === 'CT Scan',
    );

    if (indexToKeep !== -1 && !clinicFilter) {
      this.getScanClinicFilters(indexToKeep);
    } else if (indexToKeep === -1 && !clinicFilter) {
      this.getScanClinicFilters(indexToKeep);
    }
  }

  private getClinicFilters(indexToKeep: number): void {
    // Extract the single object into a temporary array
    const targetObject = this.filterItems.splice(indexToKeep, 1);

    // Clear the remaining elements from the original array
    this.filterItems.length = 0;

    // Push the saved object back in
    this.filterItems.push(...targetObject);
    const clinicFilters = new ClinicSearchFilter(
      this._route,
      this._cdRef,
      this._router,
      this._uiUtilService,
    );

    if (this.currentQueryParams['lc']) {
      if (typeof this.currentQueryParams['lc'] === 'string') {
        const value = this.currentQueryParams['lc'];
        const index = this.locationItems.findIndex(
          (item) => item.value == value,
        );
        if (this.locationItems && this.locationItems[index]) {
          this.locationItems[index].checked = true;
          this._cdRef.markForCheck();
        }
      } else {
        this.currentQueryParams['lc'].forEach((element: any) => {
          this.locationItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }
    if (this.currentQueryParams['c'] === undefined) {
      clinicFilters.categoryItems[0].checked = true;
    }

    if (this.currentQueryParams['c']) {
      if (typeof this.currentQueryParams['c'] === 'string') {
        const value = this.currentQueryParams['c'];
        const index = clinicFilters.categoryItems.findIndex(
          (item) => item.value == value,
        );
        if (clinicFilters.categoryItems[index]) {
          clinicFilters.categoryItems[index].checked = true;
        }
        this._cdRef.markForCheck();
      } else if (this.currentQueryParams['c'].length > 0) {
        const value = Number(this.currentQueryParams['c'][0]);
        const index = clinicFilters.categoryItems.findIndex(
          (item) => item.value == value,
        );
        if (clinicFilters.categoryItems[index]) {
          clinicFilters.categoryItems[index].checked = true;
        }
        this._cdRef.markForCheck();
      }
    }

    if (this.currentQueryParams['p']) {
      if (typeof this.currentQueryParams['p'] === 'string') {
        const value = this.currentQueryParams['p'];
        const index = clinicFilters.professionItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.professionItems[index]!.checked = true;
      } else {
        this.currentQueryParams['p'].forEach((element: any) => {
          clinicFilters.professionItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['s']) {
      if (typeof this.currentQueryParams['s'] === 'string') {
        const value = this.currentQueryParams['s'];
        const index = clinicFilters.specialityItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.specialityItems[index]!.checked = true;
      } else {
        this.currentQueryParams['s'].forEach((element: any) => {
          clinicFilters.specialityItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['er']) {
      if (typeof this.currentQueryParams['er'] === 'string') {
        const value = this.currentQueryParams['er'];
        const index = clinicFilters.emergencyRoomItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.emergencyRoomItems[index]!.checked = true;
      } else {
        this.currentQueryParams['er'].forEach((element: any) => {
          clinicFilters.emergencyRoomItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['hf']) {
      if (typeof this.currentQueryParams['hf'] === 'string') {
        const value = this.currentQueryParams['hf'];
        const index = clinicFilters.hospitalFacilityItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.hospitalFacilityItems[index]!.checked = true;
      } else {
        this.currentQueryParams['hf'].forEach((element: any) => {
          clinicFilters.hospitalFacilityItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['if']) {
      if (typeof this.currentQueryParams['if'] === 'string') {
        const value = this.currentQueryParams['if'];
        const index = clinicFilters.inpatientFacilityItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.inpatientFacilityItems[index]!.checked = true;
      } else {
        this.currentQueryParams['if'].forEach((element: any) => {
          clinicFilters.inpatientFacilityItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['of']) {
      if (typeof this.currentQueryParams['of'] === 'string') {
        const value = this.currentQueryParams['of'];
        const index = clinicFilters.operativeFacilityItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.operativeFacilityItems[index]!.checked = true;
      } else {
        this.currentQueryParams['of'].forEach((element: any) => {
          clinicFilters.operativeFacilityItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['os']) {
      if (typeof this.currentQueryParams['os'] === 'string') {
        const value = this.currentQueryParams['os'];
        const index = clinicFilters.outpatientServiceItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.outpatientServiceItems[index]!.checked = true;
      } else {
        this.currentQueryParams['os'].forEach((element: any) => {
          clinicFilters.outpatientServiceItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['ps']) {
      if (typeof this.currentQueryParams['ps'] === 'string') {
        const value = this.currentQueryParams['ps'];
        const index = clinicFilters.physiotherapyServiceItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.physiotherapyServiceItems[index]!.checked = true;
      } else {
        this.currentQueryParams['ps'].forEach((element: any) => {
          clinicFilters.physiotherapyServiceItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['rf']) {
      if (typeof this.currentQueryParams['rf'] === 'string') {
        const value = this.currentQueryParams['rf'];
        const index = clinicFilters.radiologyFacilityItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.radiologyFacilityItems[index]!.checked = true;
      } else {
        this.currentQueryParams['rf'].forEach((element: any) => {
          clinicFilters.radiologyFacilityItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    const filterItems = clinicFilters.filterItems;
    this.filterItems = [...this.filterItems, ...filterItems];
    this._cdRef.markForCheck();
  }

  private getScanClinicFilters(indexToKeep: number): void {
    // Extract the single object into a temporary array
    const targetObject = this.filterItems.splice(indexToKeep, 1);

    // Clear the remaining elements from the original array
    this.filterItems.length = 0;

    // Push the saved object back in
    this.filterItems.push(...targetObject);

    const clinicFilters = new ScanClinicSearchFilter(
      this._route,
      this._cdRef,
      this._router,
      this._uiUtilService,
    );

    if (this.currentQueryParams['lc']) {
      if (typeof this.currentQueryParams['lc'] === 'string') {
        const value = this.currentQueryParams['lc'];
        const index = this.locationItems.findIndex(
          (item) => item.value == value,
        );
        if (this.locationItems && this.locationItems[index]) {
          this.locationItems[index].checked = true;
          this._cdRef.markForCheck();
        }
      } else {
        this.currentQueryParams['lc'].forEach((element: any) => {
          this.locationItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }
    if (this.currentQueryParams['c'] === undefined) {
      clinicFilters.categoryItems[0].checked = true;
    }

    if (this.currentQueryParams['c']) {
      if (typeof this.currentQueryParams['c'] === 'string') {
        const value = this.currentQueryParams['c'];
        const index = clinicFilters.categoryItems.findIndex(
          (item) => item.value == value,
        );
        if (clinicFilters.categoryItems[index]) {
          clinicFilters.categoryItems[index].checked = true;
        }
        this._cdRef.markForCheck();
      } else if (this.currentQueryParams['c'].length > 0) {
        const value = Number(this.currentQueryParams['c'][0]);
        const index = clinicFilters.categoryItems.findIndex(
          (item) => item.value == value,
        );
        if (clinicFilters.categoryItems[index]) {
          clinicFilters.categoryItems[index].checked = true;
        }
        this._cdRef.markForCheck();
      }
    }

    if (this.currentQueryParams['ai']) {
      if (typeof this.currentQueryParams['ai'] === 'string') {
        const value = this.currentQueryParams['ai'];
        const index = clinicFilters.advancedSpecializedImagingItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.advancedSpecializedImagingItems[index]!.checked = true;
      } else {
        this.currentQueryParams['ai'].forEach((element: any) => {
          clinicFilters.advancedSpecializedImagingItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['cs']) {
      if (typeof this.currentQueryParams['cs'] === 'string') {
        const value = this.currentQueryParams['cs'];
        const index = clinicFilters.cTScanItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.cTScanItems[index]!.checked = true;
      } else {
        this.currentQueryParams['cs'].forEach((element: any) => {
          clinicFilters.cTScanItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['dx']) {
      if (typeof this.currentQueryParams['dx'] === 'string') {
        const value = this.currentQueryParams['dx'];
        const index = clinicFilters.digitalXRayItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.digitalXRayItems[index]!.checked = true;
      } else {
        this.currentQueryParams['dx'].forEach((element: any) => {
          clinicFilters.digitalXRayItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['fi']) {
      if (typeof this.currentQueryParams['fi'] === 'string') {
        const value = this.currentQueryParams['fi'];
        const index = clinicFilters.functionalPreventiveImagingItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.functionalPreventiveImagingItems[index]!.checked = true;
      } else {
        this.currentQueryParams['fi'].forEach((element: any) => {
          clinicFilters.functionalPreventiveImagingItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['ir']) {
      if (typeof this.currentQueryParams['ir'] === 'string') {
        const value = this.currentQueryParams['ir'];
        const index = clinicFilters.interventionalRadiologyItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.interventionalRadiologyItems[index]!.checked = true;
      } else {
        this.currentQueryParams['ir'].forEach((element: any) => {
          clinicFilters.interventionalRadiologyItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['mi']) {
      if (typeof this.currentQueryParams['mi'] === 'string') {
        const value = this.currentQueryParams['mi'];
        const index = clinicFilters.mRIItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.mRIItems[index]!.checked = true;
      } else {
        this.currentQueryParams['mi'].forEach((element: any) => {
          clinicFilters.mRIItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['nm']) {
      if (typeof this.currentQueryParams['nm'] === 'string') {
        const value = this.currentQueryParams['nm'];
        const index = clinicFilters.nuclearMedicineItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.nuclearMedicineItems[index]!.checked = true;
      } else {
        this.currentQueryParams['nm'].forEach((element: any) => {
          clinicFilters.nuclearMedicineItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['psn']) {
      if (typeof this.currentQueryParams['psn'] === 'string') {
        const value = this.currentQueryParams['psn'];
        const index = clinicFilters.pETScanItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.pETScanItems[index]!.checked = true;
      } else {
        this.currentQueryParams['psn'].forEach((element: any) => {
          clinicFilters.pETScanItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['ud']) {
      if (typeof this.currentQueryParams['ud'] === 'string') {
        const value = this.currentQueryParams['ud'];
        const index = clinicFilters.ultrasoundItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.ultrasoundItems[index]!.checked = true;
      } else {
        this.currentQueryParams['ud'].forEach((element: any) => {
          clinicFilters.ultrasoundItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    if (this.currentQueryParams['xy']) {
      if (typeof this.currentQueryParams['xy'] === 'string') {
        const value = this.currentQueryParams['xy'];
        const index = clinicFilters.xRayItems.findIndex(
          (item) => item.value == value,
        );
        clinicFilters.xRayItems[index]!.checked = true;
      } else {
        this.currentQueryParams['xy'].forEach((element: any) => {
          clinicFilters.xRayItems
            .filter((id) => id!.value == element)
            .map((id) => {
              id!.checked = true;
              this._cdRef.markForCheck();
            });
        });
      }
    }

    const filterItems = clinicFilters.filterItems;
    this.filterItems = [...this.filterItems, ...filterItems];
    this._cdRef.markForCheck();
  }

  private _getDoctorsList(
    pageCounter: number,
    filter: Partial<IKeywords>,
  ): void {
    this.loading = true;
    this._searchService
      .getDoctorsList(pageCounter, filter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (list: Array<IDoctors>) => {
          this.isListEmpty = list.length < 20 ? true : false;
          this._updateBindings(list);
        },
        (error) => {
          AppLogger.Error(error);
        },
        () => {
          this.loading = false;
        },
      );
  }

  private _getBodyScanDoctorsList(
    pageCounter: number,
    filter: Partial<IBodyScanKeywords>,
  ): void {
    this.loading = true;
    this._searchService
      .getBodyScanDoctorsList(pageCounter, filter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (list: Array<Partial<IBodyScanDoctors>>) => {
          this.isListEmpty = list.length < 20 ? true : false;
          this._updateBindings(list);
        },
        (error) => {
          AppLogger.Error(error);
        },
        () => {
          this.loading = false;
        },
      );
  }

  public onItemSelected(
    item: Checkbox,
    category: FilterCategories,
    items: Array<number>,
  ): void {
    this.cards = [];
    switch (category) {
      case FilterCategories.Location:
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams: { l: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      case FilterCategories.LocationCity:
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams: { lc: items },
          queryParamsHandling: 'merge', // or 'preserve'
        });
        break;
      default:
        break;
    }
  }

  public getClinicLocationFilter(locationId: number): void {
    this.locationItems = [];
    this._filterService
      .getLocationFilter(locationId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (locations: Array<string>) => {
          if (this.locationItems.length === 0) {
            locations.forEach((location) => {
              this.locationItems.push({
                label: location,
                value: location,
              });
            });
          }
          this.filterItems = this.filterItems.filter(
            (filter, index) => filter.title !== 'Location',
          );

          if (!this.filterItems.some((filter) => filter.title === 'Location')) {
            this.filterItems.unshift({
              title: 'Location',
              filters: {
                list: () =>
                  this.locationItems.sort((a, b) =>
                    a.label.localeCompare(b.label),
                  ),
                type: this.radioFilter,
              },
              onItemSelected: (id: any) => {
                this.onItemSelected(id, FilterCategories.LocationCity, [
                  id.value,
                ]);
              },
              expanded: true,
            });

            this.filterItems.forEach((filter, index) => {
              if (filter.title === 'Location') {
                if (typeof this.currentQueryParams['lc'] === 'string') {
                  const value = this.currentQueryParams['lc'];
                  const index = this.locationItems.findIndex(
                    (item) => item.value == value,
                  );
                  filter.selectedItem = this.locationItems[index].value;
                  this._cdRef.markForCheck();
                }
              }
            });
          }
        },
        (err) => {},
        () => {},
      );
  }

  public getBodyScanLocationFilter(locationId: number): void {
    this.locationItems = [];
    this._filterService
      .getBodyScanLocationFilter(locationId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (locations: Array<string>) => {
          if (this.locationItems.length === 0) {
            locations.forEach((location) => {
              this.locationItems.push({
                label: location,
                value: location,
              });
            });
          }
          this.filterItems = this.filterItems.filter(
            (filter, index) => filter.title !== 'Location',
          );

          if (!this.filterItems.some((filter) => filter.title === 'Location')) {
            this.filterItems.unshift({
              title: 'Location',
              filters: {
                list: () =>
                  this.locationItems.sort((a, b) =>
                    a.label.localeCompare(b.label),
                  ),
                type: this.radioFilter,
              },
              onItemSelected: (id: any) => {
                this.onItemSelected(id, FilterCategories.LocationCity, [
                  id.value,
                ]);
              },
              expanded: true,
            });

            this.filterItems.forEach((filter, index) => {
              if (filter.title === 'Location') {
                if (typeof this.currentQueryParams['lc'] === 'string') {
                  const value = this.currentQueryParams['lc'];
                  const index = this.locationItems.findIndex(
                    (item) => item.value == value,
                  );
                  filter.selectedItem = this.locationItems[index].value;
                  this._cdRef.markForCheck();
                }
              }
            });
          }
        },
        (err) => {},
        () => {},
      );
  }

  private _updateBindings(
    list: Array<IDoctors> | Array<Partial<IBodyScanDoctors>>,
  ): void {
    if (!this.loadMore) {
      this.cards = [];
    }
    if (list) {
      this.listObj = this._tranformService.updateLGCardObject(list);
      this.listObj.forEach((item) => {
        item.onArrowClick = (item: any) => this.onArrowClick(item);
        item.showBookmark = true;
      });

      this.cards.push({
        items: this.listObj,
        type: CardType.lg,
      });
    } else {
      this.cards = [];
    }
    this._cdRef.markForCheck();
  }

  private onArrowClick(item: any): void {
    const route = `/appointment/${item._id}`;
    this._router
      .navigate([route], { queryParamsHandling: 'preserve' })
      .catch((err: string | Error) => AppLogger.Error(err));
  }
}
